import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CATEGORIES, SUBCATEGORIES } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { UshqimForm } from "@/components/UshqimForm";
import { BanimForm } from "@/components/BanimForm";
import { UdhetimForm } from "@/components/UdhetimForm";
import { DyqanForm } from "@/components/DyqanForm";
import { PuneForm } from "@/components/PuneForm";
import { SherbimForm } from "@/components/SherbimForm";
import { GenericForm } from "@/components/GenericForm";
import { ReklameForm } from "@/components/ReklameForm";
import { FormCfgProvider } from "@/lib/formcfg";
import { Megaphone, UserCheck } from "lucide-react";
import api, { setOnBehalf } from "@/lib/api";
import { can } from "@/lib/perms";
import { track } from "@/lib/track";

function OnBehalfBanner({ person }) {
  return (
    <div data-testid="on-behalf-banner" className="mx-4 mt-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-3">
      <UserCheck className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="text-sm text-amber-800 min-w-0">Po publikon për: <b className="truncate">{person.name} {person.surname}</b><div className="text-xs text-amber-700 truncate">{person.email} · do të shkojë për aprovim</div></div>
    </div>
  );
}

export default function PublikoPage() {
  const nav = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [cat, setCat] = useState(null);
  const [params] = useSearchParams();
  const per = can(user, "listings") ? params.get("per") : null;
  const [person, setPerson] = useState(null);

  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  useEffect(() => {
    setOnBehalf(per);
    if (per) api.get(`/admin/users/${per}`).then((r) => setPerson(r.data.user)).catch(() => setPerson(null));
    return () => setOnBehalf(null);
  }, [per]);

  if (!user || user === false) return <div className="p-8 text-center text-slate-500">Kyçu për të publikuar një shpallje.</div>;

  if (user.account_type === "private" && !per) return (
    <div className="p-8 text-center text-slate-500" data-testid="private-no-publish">
      Vetëm llogaritë e biznesit mund të publikojnë shpallje.
    </div>
  );

  if (!cat) {
    return (
      <>
      {person && <OnBehalfBanner person={person} />}
      <div className="gs-fade px-4 pt-4">
        <h1 className="text-2xl font-extrabold text-slate-900">Çfarë dëshiron të publikosh?</h1>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {CATEGORIES.filter((c) => !c.route).map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.key} data-testid={`publish-cat-${c.key}`} onClick={() => setCat(c)} className={`bg-gradient-to-br ${c.grad} text-white rounded-3xl p-5 text-left shadow-md hover:-translate-y-1 transition-all`}>
                <Icon className="w-8 h-8 mb-3" />
                <div className="font-bold">{c.label}</div>
              </button>
            );
          })}
          <button data-testid="publish-cat-komunitete" onClick={() => nav("/komunitete")} className="bg-gradient-to-br from-cyan-400 to-sky-500 text-white rounded-3xl p-5 text-left shadow-md col-span-2">
            <div className="font-bold">💬 Shkruaj një bisedë</div>
            <div className="text-xs text-white/85">Shko te komunitetet</div>
          </button>
          <button data-testid="publish-cat-reklame" onClick={() => setCat({ key: "reklame", label: "Reklamë" })} className="bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white rounded-3xl p-5 text-left shadow-md col-span-2 flex items-center gap-3">
            <Megaphone className="w-8 h-8 shrink-0" />
            <div>
              <div className="font-bold">Reklamë</div>
              <div className="text-xs text-white/85">Publiko një reklamë · 🇽🇰 🇦🇱 🇲🇰 🇲🇪</div>
            </div>
          </button>
        </div>
      </div>
      </>
    );
  }

  const done = (id) => { track("listing_create", { cat: cat.key, lid: id }); nav(`/shpallje/${id}`); };
  const form = <FormCfgProvider key={cat.key} cat={cat.key}>{renderForm(cat, done)}</FormCfgProvider>;
  return person ? <><OnBehalfBanner person={person} />{form}</> : form;
}

function renderForm(cat, done) {
  if (cat.key === "ushqim") return <UshqimForm category={cat} onDone={done} />;
  if (cat.key === "banim") return <BanimForm onDone={done} />;
  if (cat.key === "udhetim") return <UdhetimForm onDone={done} />;
  if (cat.key === "dyqan") return <DyqanForm onDone={done} />;
  if (cat.key === "pune") return <PuneForm onDone={done} />;
  if (cat.key === "sherbime") return <SherbimForm onDone={done} />;
  if (cat.key === "reklame") return <ReklameForm onDone={done} />;
  if (cat.custom) return <UshqimForm key={cat.key} catKey={cat.key} heading={cat.label} subtitle={cat.tagline || `Publiko në ${cat.label}`} subs={SUBCATEGORIES[cat.key] || ["Të tjera"]} onDone={done} />;
  return <GenericForm category={cat} onDone={done} />;
}
